import { clsx } from "clsx";
import Konva from "konva";
import { transparentize } from "polished";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHover } from "react-aria";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { TriggerButton, TriggerButtonGroup, TriggerButtonSize } from "widgets";

import styleTrack from "../styles/Track.module.scss";

import { type SetTooltipContent } from "../GenomeViewerTracksList.js";
import { TrackHeader } from "../TrackHeader.js";
import { ProfileRows } from "./VariantTrackHeader.js";
import { groupsByProject } from "@/app-utils/entity.ts";
import { useTrackInteraction, useTrackTooltip } from "@/app-utils/hooks/useTrackTooltip.ts";
import { normalizedLength, rangesIntersect } from "@/app-utils/range.ts";
import { parse } from "@/app-utils/rsql.ts";
import { type ISelectionModel } from "@/app-utils/selection-model.ts";
import { capitalizeFirstLetter, conditionallyPluralizeTrackLabel } from "@/app-utils/string.ts";
import { type ICreateVariantShapeParams, createVariantShape } from "@/app-utils/track-shapes/shape-variant.tsx";
import { variantShapePileupParams } from "@/app-utils/track-shapes/shapes-settings.ts";
import { type IPileup, type IPileupVariant } from "@/app-utils/track-shapes/track-pileup-factory.ts";
import {
  type IFeatureInteraction,
  drawMessageOnTrack,
  getMutationShapeRenderer,
} from "@/app-utils/track-shapes/track-shapes-konva-renderers.ts";
import { getPileupHeight } from "@/app-utils/track-shapes/track-utils.ts";
import { type Dictionary } from "@/app-utils/type.ts";
import {
  type Contig,
  type GenomeBrowserStatistics,
  type Group,
  type Project,
  type Range,
  type Variant,
  type VariantAnalysisGroup,
} from "@/graphql.ts";
import {
  type IMutantGenome,
  type ISortableTrack,
  getTableSpecFromTrackUsingGenomeInformation,
  selectSingleFeature,
} from "@/redux/modules/genome-browser.ts";
import {
  type ITableColumn,
  getTableDataSlice,
  resetAllFiltersAndReloadTable,
  sameTableSpec,
} from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { GREY_COLOR, SPECIAL_COLOR } from "@/settings/colors-settings.ts";
import { CANVAS_TRACK_MARGIN } from "@/settings/genome-viewer-settings.ts";
import { FEATURE_DISPLAY_NAME, TRACK_TYPE, UNKNOWN_GENOME_ID } from "@/settings/settings.ts";

interface IVariantTrackProps {
  track: ISortableTrack;
  trackHeight: number;
  pileup: IPileup;
  overflow: boolean;
  empty?: boolean; // IS THIS POSSIBLE?
  selectedMutantGenomes: IMutantGenome[]; // selected genomes of the selected analysis group
  filterExpression: string;
  communityAnnotation: Dictionary<Group[]>;
  communityAnnotationOverflow: boolean;
  handleCustomize?: (trackType: string) => void;
  setTooltipContent: SetTooltipContent;
}

const VariantTrack: FC<IVariantTrackProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const ref = useRef<HTMLDivElement>(null);

  const tableQuerySpec = useMemo(
    () => dispatch(getTableSpecFromTrackUsingGenomeInformation(props.track)),
    [props.track],
  );
  const statistics = useSelector<IAppState, GenomeBrowserStatistics>(
    (state) => state.genomeBrowser.statistics,
    shallowEqual,
  );

  const selectionModel = useSelector<IAppState, ISelectionModel>((state) => {
    const tableData = getTableDataSlice(state.tableData, tableQuerySpec);
    return tableData?.selectionModel;
  }, shallowEqual);

  const tableColumns = useSelector<IAppState, ITableColumn[]>((state) => {
    const tableData = getTableDataSlice(state.tableData, tableQuerySpec);
    return tableData?.columns;
  }, shallowEqual);

  const { hoverProps, isHovered } = useHover({});

  const [messageOnTopMargin, setMessageOnTopMargin] = useState<number>();
  const range = useSelector<IAppState, Range>((state) => state.genomeBrowser.range, shallowEqual);
  const width = useSelector<IAppState, number>((state) => state.genomeBrowser.layout.trackWidth, shallowEqual);
  const height = props.trackHeight + CANVAS_TRACK_MARGIN * 2;

  const refGenomeId = useSelector<IAppState, number>(
    (state) => state.auth.session?.params.referenceGenome,
    shallowEqual,
  );
  const contig = useSelector<IAppState, Contig>((state) => state.genomeBrowser.contig, shallowEqual);
  const isDerivedVariantsMutantView: boolean =
    props.track.type === TRACK_TYPE.VARIANT &&
    contig.organism.id !== UNKNOWN_GENOME_ID &&
    contig.organism.id !== refGenomeId;

  // -- Layers
  // Used to draw shapes
  const layer = useMemo(() => new Konva.Layer({ clearBeforeDraw: true, y: CANVAS_TRACK_MARGIN }), []);
  // Used to draw hover effect
  const interactionLayer = useMemo(
    () => new Konva.Layer({ clearBeforeDraw: true, listening: false, y: CANVAS_TRACK_MARGIN }),
    [],
  );
  // Used to draw above all, like "Loading...", "Zoom-in...", etc.
  const textLayer = useMemo(
    () => new Konva.Layer({ clearBeforeDraw: true, listening: true, y: CANVAS_TRACK_MARGIN }),
    [],
  );
  // Used to show the highlight/hover areas
  const backgroundLayer = useMemo(
    () => new Konva.Layer({ clearBeforeDraw: true, listening: false, y: CANVAS_TRACK_MARGIN }),
    [],
  );

  const callTooltip = useTrackTooltip(layer, props.setTooltipContent);

  const { renderMutationShape, renderMutationConnector, renderProfileBox } = useMemo(() => {
    return getMutationShapeRenderer({
      layer,
      normalize: normalizedLength(width, range.start - 1, range.stop, Math.ceil),
      interactionLayer,
      visibleRange: range,
    });
  }, [layer, width, range]);
  const drawTrackMessage = drawMessageOnTrack(textLayer);

  // setup
  useEffect(() => {
    // We can have access to the stage from the layer. No need to keep it globally.
    const stage = new Konva.Stage({ container: ref.current, width, height });
    stage.add(backgroundLayer);
    stage.add(layer);
    stage.add(interactionLayer);
    stage.add(textLayer);

    return function cleanup() {
      stage.destroy();
    };
  }, []);

  // Resize canvas
  useEffect(() => {
    const stage = layer.getStage();
    if (stage == undefined) {
      return; // probably no canvas yet
    }

    stage.width(width);
    stage.height(height);
  }, [width, height]);

  const getGroups = useCallback<(p: IPileupVariant) => Map<Project, Group[]>>(
    (pileupVariant) => {
      const groups = [];

      pileupVariant.variants.forEach((v) => {
        const specificGroups: Group[] = props.communityAnnotation?.[(v as Variant).mutId || v.id];
        if (specificGroups) {
          groups.push(...specificGroups);
        }
      });

      return groupsByProject(groups);
    },
    [props.communityAnnotation],
  );

  const [hoveredMutantGenomeId, setHoveredMutantGenomeId] = useState(-1);

  const [hoverBarY, highlightBarY] = useMemo(() => {
    const pos = (index: number): number | null =>
      index >= 0
        ? (props.pileup?.variants?.[0]?.profileOffset ??
            (props.overflow ? 20 : getPileupHeight(props.pileup) + (props.pileup?.pileupParams.lanePadding ?? 0) * 2)) +
          variantShapePileupParams.boxHeight * index
        : // + (trackMessage.push ? MESSAGE_HEIGHT : 0)
          null;

    const hoverIndex = props.selectedMutantGenomes.findIndex((g) => g.id === hoveredMutantGenomeId);
    const highlightIndex = props.selectedMutantGenomes.findIndex((g) => g.highlighted);

    return [pos(hoverIndex), pos(highlightIndex)];
  }, [props.selectedMutantGenomes, hoveredMutantGenomeId, props.overflow, props.pileup /*, trackMessage.push*/]);

  const showFilterIcon = parse(props.filterExpression) != null;

  useEffect(() => {
    const stage = layer.getStage();
    if (stage == undefined) return;
    backgroundLayer.destroyChildren();
    backgroundLayer.clearCache();

    const crisp = 0.5;
    const barHeight: number = props.pileup?.pileupParams.boxHeight ?? 0;
    if (highlightBarY != undefined) {
      const highlightShape = new Konva.Rect({
        x: 0,
        y: highlightBarY - crisp,
        width: width,
        height: barHeight,
        fill: transparentize(0.85, SPECIAL_COLOR.ACTIVE),
        listening: false,
      });
      backgroundLayer.add(highlightShape);
    }

    if (hoverBarY != undefined) {
      const hoverShape = new Konva.Rect({
        x: 0,
        y: hoverBarY - crisp,
        width: width,
        height: barHeight,
        fill: transparentize(0.85, GREY_COLOR.COOL_GREY),
        listening: false,
      });
      backgroundLayer.add(hoverShape);
    }

    backgroundLayer.batchDraw();
  }, [hoverBarY, highlightBarY, width]);

  const interactedFeatureId = useSelector<IAppState, number>((state) => {
    const highlightedTrackFeature = state.genomeBrowser.highlightedTrackFeature;
    return tableQuerySpec && sameTableSpec(tableQuerySpec, highlightedTrackFeature.tableQuerySpec)
      ? highlightedTrackFeature.id
      : null;
  }, shallowEqual);
  const [variantShapesMap, setVariantShapesMap] = useState<Map<number, IFeatureInteraction>>(null);
  useTrackInteraction(interactedFeatureId, variantShapesMap);

  useEffect(() => {
    const stage = layer.getStage();
    if (stage == undefined) return;

    // Clean layer.
    layer.destroyChildren();
    textLayer.destroyChildren();
    interactionLayer.destroyChildren();

    // Remove cache and set default position because of drag event
    layer.clearCache();
    layer.x(0);
    textLayer.clearCache();
    textLayer.x(0);
    interactionLayer.clearCache();
    interactionLayer.x(0);

    // reset stage height in case we showed a message on top
    stage.height(height);
    setMessageOnTopMargin(0);

    // see message below ""Zoom in to see expression overlays""
    layer.y(CANVAS_TRACK_MARGIN);
    interactionLayer.y(CANVAS_TRACK_MARGIN);

    const type = props.track.type;
    const typeString = FEATURE_DISPLAY_NAME[type] ?? type;

    if (props.pileup?.variants?.length && props.selectedMutantGenomes.length === 0 && !isDerivedVariantsMutantView) {
      const { shape } = drawTrackMessage("Select genomes to display the variant profiles", {
        stickToBottom: true,
        interactiveStyle: true,
      });
      shape.on("click", (_) => {
        props.handleCustomize(props.track.type);
      });
    } else if (props.overflow) {
      drawTrackMessage(`Zoom in to see ${typeString}s`, { resetHeight: true });
    } else if (props.empty) {
      drawTrackMessage(`No ${typeString}s available`, { resetHeight: true });
    } else if (props.communityAnnotation && props.communityAnnotationOverflow) {
      // We draw the message on top of the track
      const { textTopMargin } = drawTrackMessage("Zoom in to see community annotation", { stickToTop: true });
      const messageTopMargin = textTopMargin + 5;
      layer.y(messageTopMargin + layer.y()); // 5px margin + .y() should be CANVAS_TRACK_MARGIN
      interactionLayer.y(messageTopMargin + interactionLayer.y());
      stage.height(height + messageTopMargin); // make the canvas higher
      setMessageOnTopMargin(messageTopMargin);
    }

    // If an overflow happens to be there but the data are still present, then we just clean the tracks and show the message (see above)
    if (!props.overflow && !props.empty) {
      const variantShapes = new Map<number, IFeatureInteraction>();

      props.pileup?.variants
        ?.filter((pileupVariant: IPileupVariant) => rangesIntersect(pileupVariant.variants[0].region.range, range))
        // sort by selection so that the selected variants are drawn last, in order to show the borders in a selected style
        ?.sort((pileupVariantA, pileupVariantB) => {
          const selection = selectionModel?.selectedIds;
          if (!selection?.length) {
            return 0;
          }
          const ASelection = pileupVariantA.variants.find((variant: Variant) => selection.includes(variant.id));
          const BSelection = pileupVariantB.variants.find((variant: Variant) => selection.includes(variant.id));
          if ((ASelection && BSelection) || (!ASelection && !BSelection)) {
            return 0;
          }
          return ASelection ? 1 : -1;
        })
        .forEach((variant) => {
          const groups = getGroups(variant);

          const selected = selectionModel
            ? variant.variants
                .filter((variant: Variant) => selectionModel.selectedIds.includes(variant.id))
                .map((v: Variant) => v.id)
            : [];

          const params: ICreateVariantShapeParams = {
            pileupVariant: variant,
            pileupParams: props.pileup.pileupParams,
            selectedIds: selected,
            callTooltip,
            renderMutationShape: renderMutationShape,
            renderConnector: renderMutationConnector,
            renderProfileBox: renderProfileBox,
            groups,
            mutantGenomes: props.selectedMutantGenomes,
            dispatch,
          };
          const { interactiveShape, doInteract } = createVariantShape(params);
          variant.variants.map((variant) => variantShapes.set(variant.id, doInteract));
          interactiveShape.on("click", ({ evt }) => {
            dispatch(
              selectSingleFeature(
                variant.variants.map((variant) => variant.id),
                tableQuerySpec,
                selected.length === 0,
                evt.ctrlKey || evt.metaKey,
              ),
            );
          });
        });
      setVariantShapesMap(variantShapes);
    }

    // TODO layer.cache({ x: -width, y: 0, width: width * 3, height });
    layer.batchDraw();
    textLayer.batchDraw();
    interactionLayer.batchDraw();
  }, [
    props.pileup,
    range,
    selectionModel,
    props.selectedMutantGenomes.length,
    props.empty,
    props.communityAnnotation,
    props.communityAnnotationOverflow,
    props.overflow,
    props.track.type,
  ]);

  const trackConfigurationDisabled =
    (props.track.type === TRACK_TYPE.VARIANT && !statistics?.hasGenomeAnalysis) ||
    (props.track.type === TRACK_TYPE.VCF_VARIANT && !statistics?.hasVcfGenomeAnalysis) ||
    // SEL9-1493: Make sure that the table columns are loaded to prevent unintentional variant type filter setting
    !tableColumns ||
    tableColumns.length === 0;

  return (
    <div className={clsx(styleTrack.track, { [styleTrack.track_hovered]: isHovered })} {...hoverProps}>
      <TrackHeader
        title={conditionallyPluralizeTrackLabel(props.track)}
        isTrackHovered={isHovered}
        trackIcons={
          <TriggerButtonGroup vertical>
            {props.handleCustomize && (
              <TriggerButton
                icon={"settings"}
                label={`Customize ${capitalizeFirstLetter(conditionallyPluralizeTrackLabel(props.track))} Track`}
                onPress={() => props.handleCustomize(props.track.type)}
                size={TriggerButtonSize.Small}
                isDisabled={trackConfigurationDisabled}
              />
            )}
            {showFilterIcon && (
              <TriggerButton
                icon={"filter_alt"}
                tooltip={`Some filter is currently applied to the track data.`}
                label={"Clear Filter Settings"}
                onPress={() => dispatch(resetAllFiltersAndReloadTable(tableQuerySpec))}
                size={TriggerButtonSize.Small}
              />
            )}
          </TriggerButtonGroup>
        }
      >
        {props.selectedMutantGenomes && (
          <ProfileRows
            pileup={props.pileup}
            overflow={props.overflow}
            mutantGenomes={props.selectedMutantGenomes}
            messageOnTopMargin={messageOnTopMargin}
            setHoveredMutantGenomeId={setHoveredMutantGenomeId}
            configuredPhenotypes={props.track.trackCustomization?.configuredPhenotypes}
            trackType={props.track.type}
          />
        )}
      </TrackHeader>
      <div className={clsx(styleTrack.container, styleTrack.container_no_top, styleTrack.container_no_bottom)}>
        <div ref={ref} />
      </div>
    </div>
  );
};

export { VariantTrack };
